/**
 * CESM OSINT System - Auto Refresh Control
 * Composant de contrôle de l'actualisation automatique des KPI
 * 
 * Concept & Développement: CDT (R) Jean-Baptiste GIRAUD
 * LPP Logiciel © 2026
 */
import React from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Play, Pause, Clock, Zap } from 'lucide-react';

const AutoRefreshControl = ({ 
  enabled, 
  onToggle, 
  interval, 
  onIntervalChange,
  lastUpdate 
}) => {
  const intervals = [
    { value: 30, label: '30s' },
    { value: 60, label: '1 min' },
    { value: 120, label: '2 min' },
    { value: 300, label: '5 min' },
  ];

  return (
    <div className="flex items-center gap-3 bg-slate-800/50 border border-slate-600/30 rounded-lg px-3 py-2">
      {/* Toggle Button */}
      <button
        onClick={onToggle}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md font-mono text-xs uppercase tracking-wider transition-all ${
          enabled 
            ? 'bg-green-500/20 border border-green-500/50 text-green-400' 
            : 'bg-slate-700/50 border border-slate-600/50 text-white/80 hover:text-white/80'
        }`}
        data-testid="auto-refresh-toggle"
      >
        {enabled ? (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </motion.div>
            <span>Auto ON</span>
          </>
        ) : (
          <>
            <Pause className="w-3.5 h-3.5" />
            <span>Auto OFF</span>
          </>
        )}
      </button>

      {/* Interval Selector */}
      {enabled && (
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-1"
        >
          <Clock className="w-3.5 h-3.5 text-white/40" />
          <select
            value={interval}
            onChange={(e) => onIntervalChange(Number(e.target.value))}
            className="bg-slate-700/50 border border-slate-600/50 rounded px-2 py-1 font-mono text-xs text-white/80 focus:outline-none focus:border-cyan-500/50"
            data-testid="auto-refresh-interval"
          >
            {intervals.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </motion.div>
      )}

      {/* Status indicator */}
      {enabled && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-1"
        >
          <Zap className="w-3 h-3 text-yellow-400" />
          <span className="font-mono text-xs text-white/40">LIVE</span>
        </motion.div>
      )}
    </div>
  );
};

export default AutoRefreshControl;
